import { api, Epic } from '../api';
import { navigate } from '../lib/router';
import { escapeHtml } from '../lib/utils';
import { openModal } from '../components/modal';
import { toast } from '../components/toast';
import { ProjectContext } from './project';

function epicStats(epic: Epic, ctx: ProjectContext) {
  const tasks = ctx.tasks.filter((t) => t.epicId === epic.id);
  const done = tasks.filter((t) => t.status === 'complete');
  const total = tasks.length;
  const completed = done.length;
  const pointsDone = done.reduce((s, t) => s + t.storyPoints, 0);
  const totalPoints = tasks.reduce((s, t) => s + t.storyPoints, 0);
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  return { tasks, total, completed, pointsDone, totalPoints, percent };
}

export async function renderEpics(
  content: HTMLElement,
  ctx: ProjectContext
): Promise<void> {
  const { project, epics, readOnly } = ctx;

  content.innerHTML = `
    <div class="list-toolbar">
      <p class="muted" style="margin:0;">
        Epics are higher-level goals that group related tasks together.
      </p>
      ${
        readOnly
          ? '<span class="muted" style="font-size:13px;">Read-only view</span>'
          : '<button class="btn btn-primary" id="new-epic">+ New Epic</button>'
      }
    </div>
    ${
      epics.length === 0
        ? `
      <div class="empty-state">
        <h3>No epics yet</h3>
        <p class="muted">${
          readOnly
            ? 'This project has no epics yet.'
            : 'Create an epic to group a set of related tasks under one goal.'
        }</p>
        ${
          readOnly
            ? ''
            : '<button class="btn btn-primary" id="new-epic-empty">+ New Epic</button>'
        }
      </div>
    `
        : `
      <div class="project-grid">
        ${epics
          .map((e) => {
            const s = epicStats(e, ctx);
            return `
            <a class="project-card" href="#${ctx.projectHref('epics', e.id)}">
              <h3>${escapeHtml(e.name)}</h3>
              <p>${escapeHtml(e.description) || '<span class="muted">No description</span>'}</p>
              <div style="margin-bottom:12px;">
                <div class="progress-track"><div class="progress-fill" style="width:${s.percent}%"></div></div>
                <div class="progress-label">${s.percent}% complete</div>
              </div>
              <div class="project-meta">
                <span>${s.completed}/${s.total} tasks</span>
                <span>${s.pointsDone}/${s.totalPoints} pts</span>
                <span style="margin-left:auto;">→</span>
              </div>
            </a>
          `;
          })
          .join('')}
      </div>
    `
    }
  `;

  if (readOnly) return;

  const openCreate = () => openCreateEpicModal(ctx);
  content.querySelector('#new-epic')?.addEventListener('click', openCreate);
  content.querySelector('#new-epic-empty')?.addEventListener('click', openCreate);
}

function openCreateEpicModal(ctx: ProjectContext): void {
  const { project, reload } = ctx;
  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:16px;">New Epic</h2>
    <form id="create-epic-form">
      <div class="field">
        <label>Epic name</label>
        <input class="input" name="name" placeholder="e.g. User authentication" />
      </div>
      <div class="field">
        <label>Description (Markdown supported)</label>
        <textarea class="textarea" name="description" rows="5" placeholder="What is this epic trying to achieve?"></textarea>
      </div>
      <div class="form-error hidden" id="epic-error"></div>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Create epic</button>
      </div>
    </form>
  `,
    (body, close) => {
      const form = body.querySelector('#create-epic-form') as HTMLFormElement;
      const errorEl = body.querySelector('#epic-error') as HTMLElement;
      body.querySelector('[data-close]')!.addEventListener('click', close);
      body.querySelector<HTMLInputElement>('input[name="name"]')?.focus();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.classList.add('hidden');
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim();
        if (!name) {
          errorEl.textContent = 'Epic name is required';
          errorEl.classList.remove('hidden');
          return;
        }
        try {
          const res = await api.post<{ epic: Epic }>(`/projects/${project.id}/epics`, {
            name,
            description: String(data.get('description') || ''),
          });
          toast('Epic created');
          close();
          navigate(`/projects/${project.id}/epics/${res.epic.id}`);
        } catch (e: any) {
          errorEl.textContent = e.message || 'Could not create epic';
          errorEl.classList.remove('hidden');
        }
      });
    }
  );
}
